import React from 'react';
import type { RoleHubStats } from '../../../types/role-hub';
import { buildLocationSrpPath } from '../../../utils/location/build-location-srp-path';
import { buildRoleHubPath, buildRolesIndexPath } from '../../../utils/role/role-hub-config';
import { finnishRolePhrase } from '../../../utils/seo/generate-srp-seo-meta';
import { getFinnishRoleGenitive } from '../../../utils/i18n/finnish-role-genitive';
import { HubCrossLinks } from '../../../components/hub/HubCrossLinks';
import type { HubCrossLink } from '../../../types/hub-cross-link';
import { buildCityJobsLink, buildEmployersInCityLink, cityInCopy, hubLinksHeading } from '../../../utils/seo/hub-index-links';
import { buildWorkModelHubPath } from '../../../utils/work-model/build-work-model-hub-path';
import { MIN_JOBS_FOR_INDEX } from '../../../utils/seo/generate-srp-seo-meta';

type Locale = 'en' | 'fi' | 'sv';

export interface RoleHeaderProps {
  roleHubData: RoleHubStats;
  locale: Locale;
  className?: string;
}

// Same in-code locale maps as CategoryHeader.tsx: this header is part of the server-rendered
// SRP shell, which renders without an IntlProvider.
const STRINGS: Record<Locale, {
  openNow: (n: string) => string;
  salaryLink: (role: string) => string;
  cities: string;
  otherCities: string;
  allRoles: string;
  allRoleJobs: (role: string) => string;
  remoteJobs: (role: string) => string;
  intro: (role: string, n: string) => string;
  introCity: (role: string, city: string, n: string) => string;
}> = {
  fi: {
    openNow: (n) => `${n} avointa työpaikkaa juuri nyt`,
    salaryLink: (role) => `Katso ${role} palkka`,
    cities: 'Suosituimmat kaupungit',
    otherCities: 'Muut kaupungit',
    allRoles: 'Kaikki ammatit',
    allRoleJobs: (role) => `Kaikki ${role} työpaikat Suomessa`,
    remoteJobs: (role) => `${role.charAt(0).toUpperCase()}${role.slice(1)} etätyöt`,
    intro: (role, n) =>
      `JobCrawls kokoaa ${role} työpaikat suoraan työnantajien sivuilta ja Työmarkkinatorilta yhteen hakuun: ${n} avointa paikkaa ympäri Suomea, myös etätyöt. Ilmoitukset päivittyvät päivittäin.`,
    introCity: (role, city, n) =>
      `JobCrawls kokoaa ${role} työpaikat ${city} suoraan työnantajien sivuilta ja Työmarkkinatorilta yhteen hakuun: ${n} avointa paikkaa juuri nyt. Ilmoitukset päivittyvät päivittäin.`,
  },
  sv: {
    openNow: (n) => `${n} lediga tjänster just nu`,
    salaryLink: (role) => `Se lön för ${role}`,
    cities: 'Populära städer',
    otherCities: 'Andra städer',
    allRoles: 'Alla yrken',
    allRoleJobs: (role) => `Alla jobb som ${role} i Finland`,
    remoteJobs: (role) => `${role} på distans`,
    intro: (role, n) =>
      `JobCrawls samlar lediga jobb som ${role} direkt från arbetsgivarnas sidor och Työmarkkinatori i en sökning: ${n} lediga tjänster i hela Finland, distansjobb inräknat. Annonserna uppdateras dagligen.`,
    introCity: (role, city, n) =>
      `JobCrawls samlar lediga jobb som ${role} i ${city} direkt från arbetsgivarnas sidor och Työmarkkinatori i en sökning: ${n} lediga tjänster just nu. Annonserna uppdateras dagligen.`,
  },
  en: {
    openNow: (n) => `${n} open positions right now`,
    salaryLink: (role) => `See ${role} salary`,
    cities: 'Popular cities',
    otherCities: 'Other cities',
    allRoles: 'All roles',
    allRoleJobs: (role) => `All ${role} jobs in Finland`,
    remoteJobs: (role) => `Remote ${role} jobs`,
    intro: (role, n) =>
      `JobCrawls collects ${role} jobs straight from employers' own sites and Työmarkkinatori into one search: ${n} open positions across Finland, remote roles included. Listings refresh daily.`,
    introCity: (role, city, n) =>
      `JobCrawls collects ${role} jobs in ${city} straight from employers' own sites and Työmarkkinatori into one search: ${n} open positions right now. Listings refresh daily.`,
  },
};

function formatCount(n: number, locale: Locale): string {
  return locale === 'en' ? n.toLocaleString('en-US') : n.toLocaleString('fi-FI').replace(/ /g, ' ');
}

/** H1 for the bare role page ("Sairaanhoitajan työpaikat Suomessa") or, with a city, the role x city page ("... Tampereella"). */
export function buildRoleHeaderH1(displayName: string, locale: Locale, cityName?: string | null): string {
  if (cityName) {
    const city = cityInCopy(cityName, locale);
    if (locale === 'fi') return `${finnishRolePhrase(displayName, 'työpaikat')} ${city}`;
    if (locale === 'sv') return `${displayName}, lediga jobb i ${city}`;
    return `${displayName} Jobs in ${city}`;
  }
  if (locale === 'fi') return `${finnishRolePhrase(displayName, 'työpaikat')} Suomessa`;
  if (locale === 'sv') return `${displayName}, lediga jobb i Finland`;
  return `${displayName} Jobs in Finland`;
}

/** Role name in the form the running copy needs: fi genitive lowercase, en/sv lowercase. */
function roleInCopy(displayName: string, locale: Locale): string {
  if (locale === 'fi') {
    const gen = getFinnishRoleGenitive(displayName);
    return gen === displayName.trim() ? displayName : gen.toLowerCase();
  }
  return displayName;
}

/**
 * Static SEO header for `/jobs/role/{slug}` pages: H1, live count, intro, salary cross-link
 * ("{role} palkka" is the second most searched suffix after "{role} työpaikat"), and the role x
 * city chips that link the indexable `/jobs/location/{city}/role/{slug}` combinations. All in
 * the initial server-rendered HTML. Rendered by SearchHeader.tsx.
 *
 * With `roleHubData.city` set (a role x city page) the H1 and intro name the city and the chips
 * link back up to the bare role page, the city's own hub and the employers-in-city page.
 */
export const RoleHeader: React.FC<RoleHeaderProps> = ({ roleHubData, locale, className = '' }) => {
  const t = STRINGS[locale];
  const displayName = roleHubData.displayNames[locale];
  const count = formatCount(roleHubData.jobCount, locale);
  const role = roleInCopy(displayName, locale);
  const salaryHref = roleHubData.salaryPaths?.[locale] ?? null;
  const city = roleHubData.city ?? null;
  const cityLinks: HubCrossLink[] = city
    ? [
        { kind: 'role', href: buildRoleHubPath(roleHubData.slug, locale), label: t.allRoleJobs(role) },
        buildCityJobsLink(locale, city.slug, city.name),
        ...((): HubCrossLink[] => {
          const employers = buildEmployersInCityLink(locale, city.name);
          return employers ? [employers] : [];
        })(),
      ]
    : [];
  const otherCities = city ? roleHubData.topCities.filter((c) => c.slug !== city.slug) : roleHubData.topCities;
  const remoteCount = roleHubData.remoteJobCount ?? 0;
  const remoteLink: HubCrossLink[] =
    !city && remoteCount >= MIN_JOBS_FOR_INDEX
      ? [{ kind: 'remote', href: buildWorkModelHubPath('remote', locale, { roleSlug: roleHubData.slug }), label: t.remoteJobs(role), count: remoteCount }]
      : [];

  return (
    <section
      id="role-info-header"
      className={`role-info-header relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#F8F5F0] via-[#F2EADF] to-[#C8DDCE]/50 dark:from-[#170F07] dark:via-[#2A1D0F] dark:to-[#1A2E22] p-6 sm:p-8 lg:p-12 ${className}`}
      aria-labelledby="role-header-title"
    >
      <h1
        id="role-header-title"
        className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF] mb-2"
      >
        {buildRoleHeaderH1(displayName, locale, city?.name)}
      </h1>
      {roleHubData.jobCount > 0 ? (
        <p className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-3">{t.openNow(count)}</p>
      ) : null}
      <p className="text-base sm:text-lg leading-relaxed max-w-5xl text-[#3D3529] dark:text-[#C4A882] mb-6">
        {city ? t.introCity(role, cityInCopy(city.name, locale), count) : t.intro(role, count)}
      </p>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {salaryHref ? (
          <a
            href={salaryHref}
            className="inline-flex items-center rounded-full bg-[#1E1A14] dark:bg-[#F2EADF] px-4 py-2 text-sm font-semibold text-white dark:text-[#1E1A14] hover:opacity-90 transition-opacity"
          >
            {t.salaryLink(role)}
          </a>
        ) : null}
        <a
          href={buildRolesIndexPath(locale)}
          className="inline-flex items-center rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/70 dark:bg-[#1F1509]/80 px-4 py-2 text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] hover:bg-white dark:hover:bg-[#2A1D0F] transition-colors"
        >
          {t.allRoles}
        </a>
      </div>
      {cityLinks.length > 0 || remoteLink.length > 0 ? (
        <HubCrossLinks variant="chips" links={[...remoteLink, ...cityLinks]} locale={locale} heading={hubLinksHeading(locale)} className="mb-6" />
      ) : null}
      {otherCities.length > 0 ? (
        <div>
          <h2 className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-2">{city ? t.otherCities : t.cities}</h2>
          <ul className="flex flex-wrap gap-2">
            {otherCities.map((city) => (
              <li key={city.slug}>
                <a
                  href={`/${locale}${buildLocationSrpPath(city.slug, locale, undefined, roleHubData.slug)}`}
                  className="inline-block rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/70 dark:bg-[#1F1509]/80 px-3 py-1.5 text-sm text-[#1E1A14] dark:text-[#F2EADF] hover:bg-white dark:hover:bg-[#2A1D0F] transition-colors"
                >
                  {city.name} ({formatCount(city.count, locale)})
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
};
